import React, { useState, useEffect, useCallback } from 'react';
import {
    Users, UserPlus, Trash2, Edit3, X, Check,
    ChevronDown, ChevronUp, AlertCircle, Search,
    TrendingUp, BarChart2, UserCheck, RefreshCw, Eye, Clock, FileText
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as api from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';


/**
 * AdminInstructors — Full instructor management page for admin dashboard.
 *
 * Sections:
 *  1. Stats bar (total instructors, unassigned students, total sessions)
 *  2. Instructor list with expandable student lists
 *  3. Create Instructor modal
 *  4. Assign Student modal
 */
export default function AdminInstructors() {
    const navigate = useNavigate();
    // ── Data state ─────────────────────────────────────────
    const [instructors, setInstructors] = useState([]);
    const [students, setStudents] = useState([]);   // all students (for assignment)
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    // ── UI state ───────────────────────────────────────────
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedId, setExpandedId] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedInstructor, setSelectedInstructor] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [manageForm, setManageForm] = useState(null);
    const [manageLoading, setManageLoading] = useState(false);

    // ── Create form state ──────────────────────────────────
    const [createForm, setCreateForm] = useState({
        fullName: '', email: '', password: '', phone: ''
    });
    const [createLoading, setCreateLoading] = useState(false);
    const [createError, setCreateError] = useState('');

    // ── Assign form state ──────────────────────────────────
    const [assignStudentId, setAssignStudentId] = useState('');
    const [assignLoading, setAssignLoading] = useState(false);

    // ── Confirm Modal state ────────────────────────────────
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        type: 'danger'
    });


    // ──────────────────────────────────────────────────────
    //  Fetch data
    // ──────────────────────────────────────────────────────
    const fetchInstructors = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.getAllInstructors();
            setInstructors(res.data || []);
        } catch (err) {
            setError('Failed to load instructors');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const res = await api.getInstructorStats();
            setStats(res.data || null);
        } catch (err) {
            // Stats failure is non-critical
        } finally {
            setStatsLoading(false);
        }
    }, []);

    const fetchStudents = useCallback(async () => {
        try {
            const res = await api.getAllUsers({ role: 'student' });
            setStudents(res || []);
        } catch (err) {
            // non-critical
        }
    }, []);

    useEffect(() => {
        fetchInstructors();
        fetchStats();
        fetchStudents();
    }, [fetchInstructors, fetchStats, fetchStudents]);

    // ──────────────────────────────────────────────────────
    //  Helpers
    // ──────────────────────────────────────────────────────
    const showSuccess = (msg) => {
        setSuccessMsg(msg);
        setTimeout(() => setSuccessMsg(''), 3500);
    };

    const filteredInstructors = instructors.filter(inst =>
        inst.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inst.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // ──────────────────────────────────────────────────────
    //  Create instructor
    // ──────────────────────────────────────────────────────
    const handleCreate = async (e) => {
        e.preventDefault();
        setCreateError('');
        if (!createForm.fullName || !createForm.email || !createForm.password) {
            setCreateError('Full name, email, and password are required.');
            return;
        }
        setCreateLoading(true);
        try {
            await api.createInstructor(createForm);
            setShowCreateModal(false);
            setCreateForm({ fullName: '', email: '', password: '', phone: '' });
            await fetchInstructors();
            await fetchStats();
            showSuccess('Instructor created successfully!');
        } catch (err) {
            setCreateError(err.message || 'Failed to create instructor');
        } finally {
            setCreateLoading(false);
        }
    };

    // ──────────────────────────────────────────────────────
    //  Delete instructor
    // ──────────────────────────────────────────────────────
    const handleDelete = (id) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Instructor?',
            message: 'If this instructor has academic history, the account will be safely deactivated. Otherwise it will be deleted and students unassigned.',
            type: 'danger',
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                setDeletingId(id);
                try {
                    await api.deleteInstructor(id);
                    await fetchInstructors();
                    await fetchStats();
                    showSuccess('Instructor safely removed or deactivated.');
                } catch (err) {
                    setError('Failed to delete instructor');
                } finally {
                    setDeletingId(null);
                }
            }
        });
    };

    const openManage = async (instructor) => {
        setManageLoading(true);
        try {
            const response = await api.getInstructorById(instructor._id);
            setManageForm({ ...response.data, newPassword: '' });
        } catch (err) { setError(err.message || 'Failed to load instructor details'); }
        finally { setManageLoading(false); }
    };

    const saveManage = async (event) => {
        event.preventDefault();
        setManageLoading(true);
        try {
            await api.updateInstructor(manageForm._id, { fullName: manageForm.fullName, email: manageForm.email, phone: manageForm.phone, isActive: manageForm.isActive });
            if (manageForm.newPassword) await api.resetInstructorPassword(manageForm._id, manageForm.newPassword);
            setManageForm(null);
            await Promise.all([fetchInstructors(), fetchStats()]);
            showSuccess('Instructor profile updated successfully.');
        } catch (err) { setError(err.message || 'Failed to update instructor'); }
        finally { setManageLoading(false); }
    };


    // ──────────────────────────────────────────────────────
    //  Assign student to instructor
    // ──────────────────────────────────────────────────────
    const openAssignModal = (instructor) => {
        setSelectedInstructor(instructor);
        setAssignStudentId('');
        setShowAssignModal(true);
    };

    const handleAssign = async (e) => {
        e.preventDefault();
        if (!assignStudentId) return;
        setAssignLoading(true);
        try {
            await api.assignStudentToInstructor(assignStudentId, selectedInstructor._id);
            setShowAssignModal(false);
            await fetchInstructors();
            await fetchStats();
            showSuccess('Student assigned successfully!');
        } catch (err) {
            setError('Failed to assign student');
        } finally {
            setAssignLoading(false);
        }
    };

    // ──────────────────────────────────────────────────────
    //  Unassign student
    // ──────────────────────────────────────────────────────
    const handleUnassign = (studentId, studentName) => {
        setConfirmModal({
            isOpen: true,
            title: 'Unassign Student?',
            message: `Are you sure you want to unassign ${studentName}? They will no longer have an instructor assigned.`,
            type: 'warning',
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    await api.assignStudentToInstructor(studentId, null);
                    await fetchInstructors();
                    showSuccess(`${studentName} unassigned.`);
                } catch (err) {
                    setError('Failed to unassign student');
                }
            }
        });
    };


    // ──────────────────────────────────────────────────────
    //  Render
    // ──────────────────────────────────────────────────────
    return (
        <div style={{ padding: '0', fontFamily: 'Inter, system-ui, sans-serif' }}>

            {/* ── Toast Messages ─────────────────────────────────── */}
            {successMsg && (
                <div style={{
                    position: 'fixed', top: '1.5rem', right: '1.5rem', zIndex: 9999,
                    background: '#dcfce7', border: '1px solid #86efac', borderRadius: '0.875rem',
                    padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.12)', fontWeight: 600, color: '#166534'
                }}>
                    <Check size={18} /> {successMsg}
                </div>
            )}
            {error && (
                <div style={{
                    position: 'fixed', top: '1.5rem', right: '1.5rem', zIndex: 9999,
                    background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '0.875rem',
                    padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.12)', cursor: 'pointer', fontWeight: 600, color: '#991b1b'
                }} onClick={() => setError('')}>
                    <AlertCircle size={18} /> {error} &nbsp;✕
                </div>
            )}

            {/* ── Page Header ─────────────────────────────────────── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    <h1 style={{ fontSize: '1.75rem', fontWeight: 900, color: '#0f172a', letterSpacing: '-0.04em', margin: 0 }}>
                        Instructor Management
                    </h1>
                    <p style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                        Create instructors · Assign students · Track attendance
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button
                        onClick={() => { fetchInstructors(); fetchStats(); }}
                        style={{ padding: '0.625rem 1rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, color: '#475569' }}
                    >
                        <RefreshCw size={16} /> Refresh
                    </button>
                    <button
                        onClick={() => { setCreateError(''); setShowCreateModal(true); }}
                        style={{ padding: '0.625rem 1.25rem', borderRadius: '0.75rem', border: 'none', background: 'linear-gradient(135deg, #066af2, #0550c4)', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 700, boxShadow: '0 4px 16px rgba(6,106,242,0.35)' }}
                    >
                        <UserPlus size={18} /> New Instructor
                    </button>
                </div>
            </div>

            {/* ── Stats Cards ─────────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                {[
                    { label: 'Total Instructors', value: stats?.totalInstructors ?? '—', icon: Users, color: '#dbeafe', iconColor: '#2563eb' },
                    { label: 'Unassigned Students', value: stats?.unassignedStudents ?? '—', icon: AlertCircle, color: '#fee2e2', iconColor: '#dc2626' },
                    { label: 'Active Instructors', value: instructors.filter(i => i.isActive).length, icon: UserCheck, color: '#dcfce7', iconColor: '#16a34a' },
                    { label: 'Total Hours Taught', value: stats?.instructorStats?.reduce((a, b) => a + (parseFloat(b.totalHours) || 0), 0).toFixed(1) ?? '—', icon: Clock, color: '#f3e8ff', iconColor: '#9333ea', unit: 'h' },
                ].map((card) => (
                    <div key={card.label} style={{ background: 'white', borderRadius: '1rem', padding: '1.5rem', border: '1px solid #f1f5f9', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{ width: '48px', height: '48px', borderRadius: '0.875rem', background: card.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <card.icon size={22} color={card.iconColor} />
                        </div>
                        <div>
                            <div style={{ fontSize: '1.75rem', fontWeight: 900, color: '#0f172a', lineHeight: 1 }}>{statsLoading ? '...' : card.value}{card.unit}</div>
                            <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginTop: '0.25rem' }}>{card.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Search Bar ──────────────────────────────────────── */}
            <div style={{ background: 'white', borderRadius: '1rem', padding: '1rem 1.25rem', border: '1px solid #f1f5f9', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Search size={18} color="#94a3b8" />
                <input
                    type="text"
                    placeholder="Search instructors by name or email..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{ border: 'none', outline: 'none', flex: 1, fontSize: '0.9rem', color: '#0f172a', background: 'transparent' }}
                />
            </div>

            {/* ── Instructor List ─────────────────────────────────── */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '5rem', color: '#94a3b8' }}>Loading instructors...</div>
            ) : filteredInstructors.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '5rem', color: '#94a3b8' }}>
                    <Users size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                    <p style={{ fontWeight: 600 }}>No instructors found. Create one to get started.</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {filteredInstructors.map(inst => (
                        <InstructorCard
                            key={inst._id}
                            instructor={inst}
                            isExpanded={expandedId === inst._id}
                            onToggle={() => setExpandedId(expandedId === inst._id ? null : inst._id)}
                            onDelete={() => handleDelete(inst._id)}
                            onManage={() => openManage(inst)}
                            isDeleting={deletingId === inst._id}
                            statsForInstructor={stats?.instructorStats?.find(s => s.instructorId?.toString() === inst._id?.toString())}
                            onViewReport={() => navigate(`/admin/session-report?instructorId=${inst._id}`)}
                        />
                    ))}
                </div>
            )}

            {/* ── Create Instructor Modal ─────────────────────────── */}
            {showCreateModal && (
                <Modal title="Create New Instructor" onClose={() => setShowCreateModal(false)}>
                    <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {createError && <Alert type="error">{createError}</Alert>}
                        <FormField label="Full Name *">
                            <input type="text" placeholder="e.g. Ahmed Hassan" value={createForm.fullName}
                                onChange={e => setCreateForm({ ...createForm, fullName: e.target.value })} />
                        </FormField>
                        <FormField label="Email Address *">
                            <input type="email" placeholder="instructor@codewave.com" value={createForm.email}
                                onChange={e => setCreateForm({ ...createForm, email: e.target.value })} />
                        </FormField>
                        <FormField label="Password *">
                            <input type="password" placeholder="Min. 8 characters with letters and numbers" value={createForm.password}
                                onChange={e => setCreateForm({ ...createForm, password: e.target.value })} />
                        </FormField>
                        <FormField label="Phone (optional)">
                            <input type="text" placeholder="+20 1xx xxx xxxx" value={createForm.phone}
                                onChange={e => setCreateForm({ ...createForm, phone: e.target.value })} />
                        </FormField>
                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                            <button type="button" onClick={() => setShowCreateModal(false)} style={cancelBtnStyle}>
                                Cancel
                            </button>
                            <button type="submit" disabled={createLoading} style={primaryBtnStyle}>
                                {createLoading ? 'Creating...' : 'Create Instructor'}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {manageForm && (
                <Modal title="Instructor Details" onClose={() => setManageForm(null)}>
                    <form onSubmit={saveManage} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <FormField label="Full Name"><input required value={manageForm.fullName} onChange={e => setManageForm({ ...manageForm, fullName: e.target.value })} /></FormField>
                        <FormField label="Email"><input required type="email" value={manageForm.email} onChange={e => setManageForm({ ...manageForm, email: e.target.value })} /></FormField>
                        <FormField label="Phone"><input value={manageForm.phone || ''} onChange={e => setManageForm({ ...manageForm, phone: e.target.value })} /></FormField>
                        <FormField label="New password (optional)"><input type="password" placeholder="Leave empty to keep current password" value={manageForm.newPassword} onChange={e => setManageForm({ ...manageForm, newPassword: e.target.value })} /></FormField>
                        <label style={{ display: 'flex', gap: '.5rem', alignItems: 'center', fontWeight: 700 }}><input type="checkbox" checked={manageForm.isActive} onChange={e => setManageForm({ ...manageForm, isActive: e.target.checked })} /> Active instructor account</label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '.5rem' }}>
                            <div style={{ padding: '.75rem', background: '#f8fafc', borderRadius: '.75rem' }}><b>{manageForm.students?.length || 0}</b><small style={{ display: 'block', color: '#64748b' }}>Students</small></div>
                            <div style={{ padding: '.75rem', background: '#f8fafc', borderRadius: '.75rem' }}><b>{manageForm.groups?.length || 0}</b><small style={{ display: 'block', color: '#64748b' }}>Groups</small></div>
                            <div style={{ padding: '.75rem', background: '#f8fafc', borderRadius: '.75rem' }}><b>{manageForm.courses?.length || 0}</b><small style={{ display: 'block', color: '#64748b' }}>Courses</small></div>
                        </div>
                        <p style={{ color: '#64748b', fontSize: '.8rem', margin: 0 }}>Attendance records: {manageForm.attendanceCount || 0} · Last activity: {manageForm.lastAttendanceAt ? new Date(manageForm.lastAttendanceAt).toLocaleString() : 'None'}</p>
                        <div style={{ display: 'flex', gap: '.75rem', justifyContent: 'flex-end' }}><button type="button" onClick={() => setManageForm(null)} style={cancelBtnStyle}>Cancel</button><button disabled={manageLoading} style={primaryBtnStyle}>{manageLoading ? 'Saving...' : 'Save Changes'}</button></div>
                    </form>
                </Modal>
            )}

            {/* ── Assign Student Modal ─────────────────────────────── */}
            {showAssignModal && selectedInstructor && (
                <Modal title={`Assign Student to ${selectedInstructor.fullName}`} onClose={() => setShowAssignModal(false)}>
                    <form onSubmit={handleAssign} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <p style={{ fontSize: '0.875rem', color: '#64748b', margin: 0 }}>
                            Select a student to assign to this instructor. Only unassigned students are shown.
                        </p>
                        <FormField label="Select Student">
                            <select value={assignStudentId} onChange={e => setAssignStudentId(e.target.value)}>
                                <option value="">— Choose a student —</option>
                                {students
                                    .filter(s => !s.instructorId) // show unassigned only
                                    .map(s => (
                                        <option key={s._id} value={s._id}>{s.fullName} ({s.email})</option>
                                    ))}
                            </select>
                        </FormField>
                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                            <button type="button" onClick={() => setShowAssignModal(false)} style={cancelBtnStyle}>
                                Cancel
                            </button>
                            <button type="submit" disabled={assignLoading || !assignStudentId} style={primaryBtnStyle}>
                                {assignLoading ? 'Assigning...' : 'Assign Student'}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* ── Confirm Action Modal ─────────────────────────────── */}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                type={confirmModal.type}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
            />
        </div>

    );
}

// ─────────────────────────────────────────────────────────────
//  Sub-components
// ─────────────────────────────────────────────────────────────

function InstructorCard({ instructor, isExpanded, onToggle, onDelete, onAssign, onManage, onUnassign, isDeleting, statsForInstructor, onViewReport }) {
    return (
        <div style={{ background: 'white', borderRadius: '1rem', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            {/* Card Header */}
            <div style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', cursor: 'pointer' }} onClick={onToggle}>
                {/* Avatar */}
                <div style={{ width: '48px', height: '48px', borderRadius: '0.875rem', background: 'linear-gradient(135deg, #066af2, #0550c4)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '1.25rem', flexShrink: 0 }}>
                    {instructor.fullName?.[0]?.toUpperCase() || 'I'}
                </div>
                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a' }}>{instructor.fullName}</span>
                        <span style={{
                            fontSize: '0.7rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: '999px',
                            background: instructor.isActive ? '#dcfce7' : '#fee2e2',
                            color: instructor.isActive ? '#166534' : '#991b1b'
                        }}>
                            {instructor.isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.2rem' }}>{instructor.email}</div>
                </div>
                {/* Stats pills */}
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0, marginRight: '0.5rem' }}>
                    <StatPill icon={Users} value={statsForInstructor?.studentCount ?? 0} label="Students" color="#dbeafe" iconColor="#2563eb" />
                    {statsForInstructor && (
                        <>
                            <StatPill icon={FileText} value={statsForInstructor.sessionsThisMonth} label="Sessions" color="#f3e8ff" iconColor="#9333ea" />
                            <StatPill icon={Clock} value={statsForInstructor.hoursThisMonth} label="Hours" color="#dcfce7" iconColor="#16a34a" unit="h" />
                        </>
                    )}
                </div>
                {/* Actions */}
                <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    <button
                        onClick={onManage}
                        style={{ padding: '0.5rem 0.75rem', borderRadius: '0.625rem', border: '1px solid #e2e8f0', background: 'white', color: '#475569', cursor: 'pointer', fontWeight: 700, fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                    >
                        <Edit3 size={13} /> Manage
                    </button>
                    <button
                        onClick={onViewReport}
                        style={{ padding: '0.5rem 0.75rem', borderRadius: '0.625rem', border: '1px solid #e2e8f0', background: 'white', color: '#64748b', cursor: 'pointer', fontWeight: 700, fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                        title="Detailed Report"
                    >
                        <BarChart2 size={13} /> Report
                    </button>
                    <button
                        onClick={onAssign}
                        style={{ padding: '0.5rem 0.75rem', borderRadius: '0.625rem', border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer', fontWeight: 700, fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                    >
                        <UserPlus size={13} /> Assign
                    </button>
                    <button
                        onClick={onDelete}
                        disabled={isDeleting}
                        style={{ padding: '0.5rem', borderRadius: '0.625rem', border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}
                    >
                        <Trash2 size={13} />
                    </button>
                </div>
                {/* Expand toggle */}
                <div style={{ color: '#94a3b8', marginLeft: '0.5rem' }}>
                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </div>
            </div>

            {/* Expanded Students List */}
            {isExpanded && (
                <div style={{ borderTop: '1px solid #f1f5f9', padding: '1rem 1.5rem', background: '#f8fafc' }}>
                    <h4 style={{ fontSize: '0.8rem', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.75rem' }}>
                        Assigned Students ({instructor.studentCount || 0})
                    </h4>
                    {instructor.studentCount === 0 ? (
                        <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No students assigned yet.</p>
                    ) : (
                        <p style={{ color: '#64748b', fontSize: '0.85rem', fontStyle: 'italic' }}>
                            Click "Assign" above to add more students. Use the Instructor Dashboard to view full student details.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

function StatPill({ icon: Icon, value, label, color, iconColor, unit = '' }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: color, borderRadius: '0.625rem', padding: '0.375rem 0.75rem' }}>
            <Icon size={14} color={iconColor} />
            <span style={{ fontWeight: 800, fontSize: '0.85rem', color: '#0f172a' }}>{value}{unit}</span>
            <span style={{ fontSize: '0.7rem', color: '#475569', fontWeight: 600 }}>{label}</span>
        </div>
    );
}

function Modal({ title, children, onClose }) {
    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)'
        }} onClick={onClose}>
            <div style={{
                background: 'white', borderRadius: '1.25rem', padding: '2rem', width: '100%', maxWidth: '500px',
                boxShadow: '0 25px 80px rgba(0,0,0,0.25)', margin: '1rem'
            }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                    <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 900, color: '#0f172a', letterSpacing: '-0.03em' }}>{title}</h2>
                    <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', borderRadius: '0.625rem', padding: '0.5rem', cursor: 'pointer', color: '#64748b' }}>
                        <X size={18} />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

function FormField({ label, children }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
            <div style={{ '& input, & select': { width: '100%' } }}>
                {React.cloneElement(children, {
                    style: {
                        width: '100%', padding: '0.75rem 1rem', borderRadius: '0.75rem',
                        border: '1.5px solid #e2e8f0', outline: 'none', fontSize: '0.9rem',
                        color: '#0f172a', background: '#f8fafc', boxSizing: 'border-box',
                        transition: 'border-color 0.2s',
                        ...(children.props.style || {})
                    }
                })}
            </div>
        </div>
    );
}

function Alert({ type, children }) {
    const isError = type === 'error';
    return (
        <div style={{
            padding: '0.875rem 1rem', borderRadius: '0.75rem',
            background: isError ? '#fee2e2' : '#dcfce7',
            border: `1px solid ${isError ? '#fca5a5' : '#86efac'}`,
            color: isError ? '#991b1b' : '#166534',
            fontSize: '0.875rem', fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: '0.5rem'
        }}>
            <AlertCircle size={16} /> {children}
        </div>
    );
}

// ── Button styles ────────────────────────────────────────────
const primaryBtnStyle = {
    padding: '0.75rem 1.5rem', borderRadius: '0.75rem', border: 'none',
    background: 'linear-gradient(135deg, #066af2, #0550c4)', color: 'white',
    cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem',
    boxShadow: '0 4px 16px rgba(6,106,242,0.35)'
};

const cancelBtnStyle = {
    padding: '0.75rem 1.5rem', borderRadius: '0.75rem', border: '1.5px solid #e2e8f0',
    background: 'white', color: '#475569', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem'
};
